import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { selectAppName, selectSource } from 'root-redux/selects/common'
import { selectUUID, selectUserContactEmail } from 'root-redux/selects/user'

import {
  EMAIL_VALIDATION,
  NAME_VALIDATION,
} from 'modules/contactForm/constants'

import { eventLogger } from 'services/eventLogger.service'

import closeIcon from 'assets/images/close-icon-circle.svg'
import errorIcon from 'assets/images/error.png'

import { APP_COLORS, AppName, EMAIL_REGEXP } from 'root-constants'

import { StyledWithdrawalModal as S } from './WithdrawalModal.styles'

interface IWithdrawalFormData {
  name: string
  surname: string
  email: string
}

type TProps = {
  isShown: boolean
  contract: string
  onClose: () => void
  onConfirm: (data: IWithdrawalFormData) => void
}

export const WithdrawalModal: React.FC<TProps> = ({
  isShown,
  contract,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation()
  const appName = useSelector(selectAppName) as AppName
  const prefillEmail = useSelector(selectUserContactEmail)
  const source = useSelector(selectSource)
  const userId = useSelector(selectUUID)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<IWithdrawalFormData>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      surname: '',
      email: prefillEmail || '',
    },
  })

  const values = watch()
  const isFormValid =
    !!values.name?.trim() &&
    !!values.surname?.trim() &&
    EMAIL_REGEXP.test(values.email?.trim() ?? '')

  const handleConfirmClick = () => {
    eventLogger.logConfirmCancellationButtonTap(source, userId)
    handleSubmit(onConfirm)()
  }

  if (!isShown) return null

  return (
    <>
      <S.Backdrop onClick={onClose} />
      <S.Sheet>
        <S.TopNav>
          <S.DragHandle />
          <S.CloseButton onClick={onClose} aria-label="Close">
            <img src={closeIcon} alt="" />
          </S.CloseButton>
        </S.TopNav>

        <S.Content>
          <S.Title>{t('withdrawal.modal.title')}</S.Title>
          <S.Subtitle>{t('withdrawal.modal.subtitle')}</S.Subtitle>

          <S.FieldWrapper>
            <S.FieldLabel htmlFor="withdrawal-name">
              {t('withdrawal.modal.nameLabel')}
            </S.FieldLabel>
            <S.Input
              id="withdrawal-name"
              type="text"
              placeholder={t('withdrawal.modal.namePlaceholder')}
              hasError={!!errors.name}
              {...register('name', { validate: NAME_VALIDATION })}
            />
            {errors.name && (
              <S.ErrorHint>
                <S.ErrorIcon src={errorIcon} alt="" />
                {t('withdrawal.modal.nameRequired')}
              </S.ErrorHint>
            )}
          </S.FieldWrapper>

          <S.FieldWrapper>
            <S.FieldLabel htmlFor="withdrawal-surname">
              {t('withdrawal.modal.surnameLabel')}
            </S.FieldLabel>
            <S.Input
              id="withdrawal-surname"
              type="text"
              placeholder={t('withdrawal.modal.surnamePlaceholder')}
              hasError={!!errors.surname}
              {...register('surname', { validate: NAME_VALIDATION })}
            />
            {errors.surname && (
              <S.ErrorHint>
                <S.ErrorIcon src={errorIcon} alt="" />
                {t('withdrawal.modal.surnameRequired')}
              </S.ErrorHint>
            )}
          </S.FieldWrapper>

          <S.FieldWrapper>
            <S.FieldLabel htmlFor="withdrawal-email">
              {t('withdrawal.modal.emailLabel')}
            </S.FieldLabel>
            <S.Input
              id="withdrawal-email"
              type="email"
              placeholder={t('withdrawal.modal.emailPlaceholder')}
              hasError={!!errors.email}
              {...register('email', { validate: EMAIL_VALIDATION })}
            />
            {errors.email && (
              <S.ErrorHint>
                <S.ErrorIcon src={errorIcon} alt="" />
                {t('withdrawal.modal.invalidEmail')}
              </S.ErrorHint>
            )}
            {!errors.email && (
              <S.EmailNote>{t('withdrawal.modal.emailNote')}</S.EmailNote>
            )}
          </S.FieldWrapper>

          <S.FieldWrapper>
            <S.FieldLabel htmlFor="withdrawal-contract">
              {t('withdrawal.modal.contractLabel')}
            </S.FieldLabel>
            <S.Input
              id="withdrawal-contract"
              type="text"
              value={contract}
              hasError={false}
              disabled
              readOnly
            />
          </S.FieldWrapper>
        </S.Content>

        <S.StickyFooter>
          <S.ConfirmButton
            brandColor={APP_COLORS[appName]}
            isDisabled={!isFormValid}
            disabled={!isFormValid}
            onClick={handleConfirmClick}
          >
            {t('withdrawal.modal.confirmButton')}
          </S.ConfirmButton>
        </S.StickyFooter>
      </S.Sheet>
    </>
  )
}
